(() => {
  if (customElements.get('taxonomy-mega-menu')) return;

  class TaxonomyMegaMenu extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.language = this.dataset.language === 'en' ? 'en' : 'nl';
      this.labelSource = this.dataset.labelSource === 'collection' ? 'collection' : 'taxonomy';
      this.showCount = this.dataset.showCount === 'true';
      this.openFirst = this.dataset.openFirst === 'true';
      this.rootLabel = this.dataset.rootLabel || 'Categorieën';
      this.viewAllLabel = this.dataset.viewAllLabel || 'Bekijk alles';
      this.backLabel = this.dataset.backLabel || 'Terug';
      this.countFormatter = new Intl.NumberFormat(this.language === 'en' ? 'en-US' : 'nl-NL');
      this.viewport = this.querySelector('[data-taxonomy-viewport]');
      this.panelsElement = this.querySelector('[data-taxonomy-panels]');
      this.emptyElement = this.querySelector('[data-taxonomy-empty]');
      this.sourceElement = this.querySelector('[data-taxonomy-source]');
      this.activePath = [];
      this.refreshFrame = null;

      this.refreshFromSource({ openFirst: this.openFirst });
      this.observeSource();
    }

    disconnectedCallback() {
      if (this.sourceObserver) this.sourceObserver.disconnect();
      if (this.refreshFrame) cancelAnimationFrame(this.refreshFrame);
      this.initialized = false;
    }

    observeSource() {
      if (!this.sourceElement || typeof MutationObserver === 'undefined') return;

      this.sourceObserver = new MutationObserver(() => {
        if (this.refreshFrame) cancelAnimationFrame(this.refreshFrame);
        this.refreshFrame = requestAnimationFrame(() => {
          this.refreshFrame = null;
          if (!this.isConnected) return;
          this.refreshFromSource();
        });
      });

      this.sourceObserver.observe(this.sourceElement, {
        attributes: true,
        childList: true,
        subtree: true,
      });
    }

    refreshFromSource({ openFirst = false } = {}) {
      const previousPath = Array.isArray(this.activePath) ? [...this.activePath] : [];
      this.nodes = new Map();
      this.roots = [];
      this.orderCounter = 0;
      this.buildTree();

      if (this.roots.length === 0) {
        this.activePath = [];
        this.emptyElement.hidden = false;
        this.viewport.hidden = true;
        this.panelsElement.replaceChildren();
        return;
      }

      this.emptyElement.hidden = true;
      this.viewport.hidden = false;
      this.activePath = this.validActivePath(previousPath);

      if ((openFirst || this.activePath.length === 0) && this.openFirst) {
        this.activePath = [];
        this.openFirstAvailablePath();
      }

      this.render();
    }

    validActivePath(path) {
      const valid = [];
      let expectedParentId = null;

      for (const id of path) {
        const node = this.nodes.get(id);
        if (!node || node.parentId !== expectedParentId || node.children.length === 0) break;
        valid.push(id);
        expectedParentId = id;
      }

      return valid;
    }

    splitPath(value) {
      return String(value || '')
        .split('>')
        .map((part) => part.trim())
        .filter(Boolean);
    }

    buildTree() {
      const entries = this.querySelectorAll('[data-taxonomy-entry]');

      entries.forEach((entry) => {
        const ids = this.splitPath(entry.dataset.pathIds);
        const namesEn = this.splitPath(entry.dataset.pathEn);
        const namesNl = this.splitPath(entry.dataset.pathNl);
        const usableLength = Math.min(ids.length, namesEn.length, namesNl.length);
        if (!usableLength) return;

        let parent = null;
        for (let index = 0; index < usableLength; index += 1) {
          const id = ids[index];
          let node = this.nodes.get(id);

          if (!node) {
            node = {
              id,
              parentId: parent ? parent.id : null,
              names: { en: namesEn[index], nl: namesNl[index] },
              collectionTitle: '',
              url: '',
              count: null,
              order: this.orderCounter++,
              children: [],
            };
            this.nodes.set(id, node);

            if (parent) {
              parent.children.push(node);
            } else {
              this.roots.push(node);
            }
          } else {
            if (!node.names.en && namesEn[index]) node.names.en = namesEn[index];
            if (!node.names.nl && namesNl[index]) node.names.nl = namesNl[index];
            if (parent && !parent.children.some((child) => child.id === node.id)) {
              parent.children.push(node);
            }
          }

          parent = node;
        }

        const linkedNode = this.nodes.get(ids[usableLength - 1]);
        if (!linkedNode) return;
        linkedNode.url = entry.dataset.url || '';
        linkedNode.collectionTitle = entry.dataset.collectionTitle || '';
        const parsedCount = Number.parseInt(entry.dataset.count || '', 10);
        linkedNode.count = Number.isFinite(parsedCount) ? parsedCount : null;
      });

      const sortTree = (nodes) => {
        nodes.sort((a, b) => a.order - b.order);
        nodes.forEach((node) => sortTree(node.children));
      };
      sortTree(this.roots);
    }

    openFirstAvailablePath() {
      let current = this.roots[0];
      while (current && current.children.length > 0) {
        this.activePath.push(current.id);
        current = current.children[0];
      }
    }

    nodeLabel(node) {
      if (this.labelSource === 'collection' && node.collectionTitle) {
        return node.collectionTitle;
      }
      return node.names[this.language] || node.names.en || node.collectionTitle || node.id;
    }

    productCountLabel(count) {
      const safeCount = Number.isFinite(count) ? Math.max(0, count) : 0;
      const formattedCount = this.countFormatter.format(safeCount);

      if (this.language === 'en') {
        return `${formattedCount} ${safeCount === 1 ? 'product' : 'products'}`;
      }

      return `${formattedCount} ${safeCount === 1 ? 'product' : 'producten'}`;
    }

    panelDefinitions() {
      const definitions = [
        {
          parent: null,
          title: this.rootLabel,
          nodes: this.roots,
          depth: 0,
        },
      ];

      this.activePath.forEach((id, index) => {
        const node = this.nodes.get(id);
        if (!node || node.children.length === 0) return;
        definitions.push({
          parent: node,
          title: this.nodeLabel(node),
          nodes: node.children,
          depth: index + 1,
        });
      });

      return definitions;
    }

    setActive(node, depth) {
      if (node.children.length === 0) {
        if (node.url) window.location.assign(node.url);
        return;
      }

      this.activePath = this.activePath.slice(0, depth);
      this.activePath[depth] = node.id;
      this.render();
      requestAnimationFrame(() => this.scrollToLatestPanel());
    }

    goBack(depth) {
      this.activePath = this.activePath.slice(0, Math.max(0, depth - 1));
      this.render();
      requestAnimationFrame(() => this.scrollToLatestPanel());
    }

    scrollToLatestPanel() {
      if (!this.viewport) return;

      const panels = this.panelsElement.querySelectorAll('[data-taxonomy-panel]');
      const latest = panels[panels.length - 1];
      if (!latest) return;

      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const latestRight = latest.offsetLeft + latest.offsetWidth;
      const maximumScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const requestedLeft = isMobile
        ? latest.offsetLeft
        : latestRight - this.viewport.clientWidth;
      const targetLeft = Math.min(maximumScroll, Math.max(0, requestedLeft));
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.viewport.scrollTo({
        left: targetLeft,
        behavior: reducedMotion ? 'auto' : 'smooth',
      });
    }

    createChevron() {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', 'taxonomy-mega-menu__chevron');
      svg.setAttribute('viewBox', '0 0 20 20');
      svg.setAttribute('aria-hidden', 'true');
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'M7 4.5 12.5 10 7 15.5');
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke', 'currentColor');
      path.setAttribute('stroke-linecap', 'round');
      path.setAttribute('stroke-linejoin', 'round');
      path.setAttribute('stroke-width', '1.7');
      svg.append(path);
      return svg;
    }

    createPanel(definition) {
      const panel = document.createElement('section');
      panel.className = 'taxonomy-mega-menu__panel';
      panel.dataset.taxonomyPanel = String(definition.depth);
      panel.setAttribute('aria-label', definition.title);

      const header = document.createElement('header');
      header.className = 'taxonomy-mega-menu__panel-header';

      const headingWrap = document.createElement('div');
      headingWrap.className = 'taxonomy-mega-menu__panel-heading-wrap';

      if (definition.depth > 0) {
        const back = document.createElement('button');
        back.type = 'button';
        back.className = 'taxonomy-mega-menu__back';
        back.textContent = `← ${this.backLabel}`;
        back.addEventListener('click', () => this.goBack(definition.depth));
        headingWrap.append(back);
      }

      const heading = document.createElement('h3');
      heading.className = 'taxonomy-mega-menu__panel-title';
      heading.textContent = definition.title;
      headingWrap.append(heading);
      header.append(headingWrap);

      if (definition.parent && definition.parent.url) {
        const viewAll = document.createElement('a');
        viewAll.className = 'taxonomy-mega-menu__view-all';
        viewAll.href = definition.parent.url;
        viewAll.textContent = this.viewAllLabel;
        header.append(viewAll);
      }

      panel.append(header);

      const list = document.createElement('ul');
      list.className = 'taxonomy-mega-menu__list';
      list.setAttribute('role', 'list');

      definition.nodes.forEach((node) => {
        const item = document.createElement('li');
        const hasChildren = node.children.length > 0;
        const isActive = this.activePath[definition.depth] === node.id;
        const control = document.createElement(hasChildren ? 'button' : 'a');
        control.className = `taxonomy-mega-menu__item${hasChildren ? '' : ' taxonomy-mega-menu__item--leaf'}${isActive ? ' is-active' : ''}`;

        if (hasChildren) {
          control.type = 'button';
          control.setAttribute('aria-expanded', isActive ? 'true' : 'false');
          control.addEventListener('click', () => this.setActive(node, definition.depth));
        } else if (node.url) {
          control.href = node.url;
        } else {
          control.href = '#';
          control.setAttribute('aria-disabled', 'true');
          control.addEventListener('click', (event) => event.preventDefault());
        }

        const label = document.createElement('span');
        label.className = 'taxonomy-mega-menu__item-label';
        label.textContent = this.nodeLabel(node);

        if (this.showCount && hasChildren && node.count !== null) {
          const count = document.createElement('span');
          count.className = 'taxonomy-mega-menu__count';
          count.textContent = `(${this.countFormatter.format(node.count)})`;
          label.append(count);
        }

        control.append(label);
        if (hasChildren) {
          control.append(this.createChevron());
        } else {
          const leafCount = document.createElement('span');
          leafCount.className = 'taxonomy-mega-menu__leaf-count';
          leafCount.textContent = this.productCountLabel(node.count);
          control.append(leafCount);
        }

        item.append(control);
        list.append(item);
      });

      panel.append(list);
      return panel;
    }

    render() {
      const fragment = document.createDocumentFragment();
      this.panelDefinitions().forEach((definition) => {
        fragment.append(this.createPanel(definition));
      });
      this.panelsElement.replaceChildren(fragment);
    }
  }

  customElements.define('taxonomy-mega-menu', TaxonomyMegaMenu);
})();